// src/utils/fetchWithAuth.js
import { apiUrl } from "@/lib/api";

/**
 * ============================================================================
 * TOKEN MANAGEMENT
 * ============================================================================
 */

function getStoredToken() {
  return (
    localStorage.getItem("access") ||       // JWT access token
    localStorage.getItem("bearer") ||       // Bearer token
    localStorage.getItem("authToken") ||    // DRF Token
    localStorage.getItem("token") ||        // Legacy
    null
  );
}

function getAuthHeader(token) {
  // Use Bearer for JWT access tokens, Token for DRF token auth
  const isJWT = localStorage.getItem("access");
  return isJWT ? `Bearer ${token}` : `Token ${token}`;
}

export function setAuthToken(token, type = "access") {
  const storageKey = {
    access: "access",
    bearer: "bearer",
    token: "authToken",
  }[type] || "authToken";
  
  localStorage.setItem(storageKey, token);
  clearMeCache();
}

export function clearAuth() {
  localStorage.removeItem("access");
  localStorage.removeItem("refresh");
  localStorage.removeItem("bearer");
  localStorage.removeItem("authToken");
  localStorage.removeItem("token");
  clearMeCache();
}

export function isAuthenticated() {
  return !!getStoredToken();
}

/**
 * ============================================================================
 * /ME ENDPOINT CACHING
 * ============================================================================
 */

let _meCache = { timestamp: 0, data: null };
const ME_CACHE_TTL_MS = 5000; // 5 seconds

function clearMeCache() {
  _meCache = { timestamp: 0, data: null };
}

function shouldUseMeCache(url, method) {
  if (method !== "GET") return false;
  if (!/\/api\/accounts\/me\/?$/.test(url)) return false;
  
  const age = Date.now() - _meCache.timestamp;
  return age < ME_CACHE_TTL_MS && _meCache.data !== null;
}

function updateMeCache(data) {
  _meCache = { timestamp: Date.now(), data };
}

/**
 * ============================================================================
 * MAIN FETCH FUNCTION
 * ============================================================================
 */

/**
 * Enhanced fetch wrapper with authentication and automatic JSON handling
 * 
 * @param {string|Request} input - URL string or Request object
 * @param {object} options - Fetch options
 * @param {object} options.json - Convenience parameter for JSON body (auto-stringified)
 * @param {*} options.body - Raw body data (FormData, string, etc.)
 * @param {object} options.headers - Additional headers
 * @param {string} options.method - HTTP method
 * @returns {Promise<any>} Parsed response data
 */
// DRF field errors arrive as {field: ["message"]}. Without this every form
// showed "Request failed" and the reason stayed in the console (2026-10-08).
function firstFieldError(data) {
  for (const v of Object.values(data)) {
    if (typeof v === "string" && v) return v;
    if (Array.isArray(v) && typeof v[0] === "string") return v[0];
  }
  return "";
}

export async function fetchWithAuth(input, options = {}) {
  // Resolve URL
  const url = apiUrl(typeof input === "string" ? input : input?.url || "");
  
  // Determine method
  const method = (
    options.method || 
    (options.json !== undefined ? "POST" : "GET")
  ).toUpperCase();

  // Prepare body
  let body = options.body;
  const hasJsonOption = options.json !== undefined;
  
  if (hasJsonOption && body === undefined) {
    body = JSON.stringify(options.json);
  }

  // Check if body is FormData
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  // Build headers
  const headers = new Headers(options.headers || {});
  
  // Set Accept header
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }

  // Add authentication token
  const token = getStoredToken();
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", getAuthHeader(token));
  }

  // Set Content-Type for JSON (but not FormData - browser handles it)
  if (
    method !== "GET" && 
    !isFormData && 
    body != null && 
    !headers.has("Content-Type")
  ) {
    headers.set("Content-Type", "application/json");
  }

  // Check /me cache
  if (shouldUseMeCache(url, method)) {
    console.log("📦 Using cached /me data");
    return _meCache.data;
  }

  // Log request (useful for debugging)
  console.log(`🌐 ${method} ${url}`);
  if (body && !isFormData) {
    console.log("📤 Request body:", body);
  }

  // Make the request. options.signal flows through to native fetch via
  // the spread below — callers using useAbortSignal() get clean
  // cancellation when their component unmounts (e.g., user clicks away
  // before the response lands).
  let response;
  try {
    response = await fetch(url, {
      ...options,
      method,
      body,
      headers,
      cache: options.cache ?? "no-store",
      credentials: options.credentials ?? "omit",
    });
  } catch (networkError) {
    // Aborts are intentional (component unmount, route change). Re-throw
    // them WITHOUT the "Cannot connect" message so callers can ignore
    // them silently. A toast saying "Cannot connect to server" after the
    // user just navigated away would be a bug.
    if (networkError?.name === "AbortError") {
      const err = new Error("Request aborted");
      err.name = "AbortError";
      err.aborted = true;
      throw err;
    }
    console.error("❌ Network error:", networkError);
    throw new Error("Cannot connect to server. Please check your internet connection.");
  }

  console.log(`✅ Response: ${response.status} ${response.statusText}`);

  // Parse response
  const contentType = response.headers.get("content-type") || "";
  let data = null;

  try {
    if (contentType.includes("application/json")) {
      const text = await response.text();
      data = text ? JSON.parse(text) : null;
    } else if (contentType.startsWith("text/")) {
      data = await response.text();
    } else {
      // Try to get some response data for error messages
      const text = await response.text();
      if (text) {
        // Check if it's actually JSON despite wrong content-type
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }
      }
    }
  } catch (parseError) {
    console.error("❌ Failed to parse response:", parseError);
    data = null;
  }

  // Handle HTTP errors
  if (!response.ok) {
    console.error(`❌ HTTP ${response.status}:`, data);

    // Extract error message
    let errorMessage = "Request failed";
    
    if (typeof data === "object" && data !== null) {
      errorMessage = 
        data.detail || 
        data.error || 
        data.message ||
        (Array.isArray(data.non_field_errors) && data.non_field_errors.join(", ")) ||
        firstFieldError(data) ||
        errorMessage;
    } else if (typeof data === "string") {
      errorMessage = data;
    }

    const error = new Error(errorMessage);
    error.status = response.status;
    error.statusText = response.statusText;
    error.data = data;
    error.url = url;
    throw error;
  }

  // Update /me cache if applicable
  if (/\/api\/accounts\/me\/?$/.test(url) && method === "GET") {
    updateMeCache(data);
  }

  return data;
}

/**
 * ============================================================================
 * CONVENIENCE WRAPPERS
 * ============================================================================
 */

/**
 * POST request wrapper
 * @param {string} url - API endpoint
 * @param {object|FormData} payload - Data to send
 * @param {object} options - Additional fetch options
 * @returns {Promise<any>}
 */
export async function postWithAuth(url, payload, options = {}) {
  const isFormDataPayload = payload instanceof FormData;
  
  return fetchWithAuth(url, {
    ...options,
    method: "POST",
    ...(isFormDataPayload ? { body: payload } : { json: payload }),
  });
}

/**
 * PUT request wrapper
 * @param {string} url - API endpoint
 * @param {object|FormData} payload - Data to send
 * @param {object} options - Additional fetch options
 * @returns {Promise<any>}
 */
export async function putWithAuth(url, payload, options = {}) {
  const isFormDataPayload = payload instanceof FormData;
  
  return fetchWithAuth(url, {
    ...options,
    method: "PUT",
    ...(isFormDataPayload ? { body: payload } : { json: payload }),
  });
}

/**
 * PATCH request wrapper
 * @param {string} url - API endpoint
 * @param {object|FormData} payload - Data to send
 * @param {object} options - Additional fetch options
 * @returns {Promise<any>}
 */
export async function patchWithAuth(url, payload, options = {}) {
  const isFormDataPayload = payload instanceof FormData;
  
  return fetchWithAuth(url, {
    ...options,
    method: "PATCH",
    ...(isFormDataPayload ? { body: payload } : { json: payload }),
  });
}

/**
 * DELETE request wrapper
 * @param {string} url - API endpoint
 * @param {object} options - Additional fetch options
 * @returns {Promise<any>}
 */
export async function deleteWithAuth(url, options = {}) {
  return fetchWithAuth(url, {
    ...options,
    method: "DELETE",
  });
}

/**
 * Raw fetch wrapper - returns Response object instead of parsed data
 * Useful when you need access to headers or want custom parsing
 * @param {string} url - API endpoint
 * @param {object} options - Fetch options
 * @returns {Promise<Response>}
 */
export async function fetchWithAuthRaw(url, options = {}) {
  const resolvedUrl = apiUrl(url);
  const method = (options.method || "GET").toUpperCase();

  const headers = new Headers(options.headers || {});
  
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }

  const token = getStoredToken();
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", getAuthHeader(token));
  }

  return fetch(resolvedUrl, {
    ...options,
    method,
    headers,
    credentials: options.credentials ?? "omit",
  });
}

/**
 * ============================================================================
 * DEBUGGING UTILITIES
 * ============================================================================
 */

/**
 * Enable/disable detailed logging
 */
let _debugMode = false;

export function enableDebugMode() {
  _debugMode = true;
  console.log("🐛 fetchWithAuth debug mode enabled");
}

export function disableDebugMode() {
  _debugMode = false;
  console.log("🐛 fetchWithAuth debug mode disabled");
}

/**
 * Get current authentication status details
 * @returns {object} Auth status information
 */
export function getAuthStatus() {
  const token = getStoredToken();
  return {
    isAuthenticated: !!token,
    tokenType: localStorage.getItem("access") ? "JWT" : "Token",
    hasToken: !!token,
    tokenPreview: token ? `${token.substring(0, 10)}...` : null,
    cacheAge: Date.now() - _meCache.timestamp,
    hasCachedMe: !!_meCache.data,
  };
}